import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EntitySelect } from '@/components/EntitySelect';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { InfoHint } from '@/components/ui/info-hint';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import type { ApiError } from '@/core/api';
import { resolveCompanyLabel, searchCompanies } from '@/features/crm/companies/select';
import { resolveContactLabel, searchContacts } from '@/features/crm/contacts/select';
import { resolveDealLabel, searchDeals } from '@/features/crm/deals/select';
import { resolveLeadLabel, searchLeads } from '@/features/crm/leads/select';
import { MemberSelect } from '@/features/members';
import { CRM_OWNER_ROLES } from '@/features/crm/constants';
import { fromLocalInput, toLocalInput } from '@/lib/datetime';
import { ACTIVITY_TYPES } from '../constants';
import type { Activity, ActivityInput } from '../types';

const schema = z.object({
  type: z.enum(['call', 'meeting', 'email', 'note', 'task']),
  subject: z.string().trim().min(1, 'Subject is required').max(255),
  notes: z.string(),
  occurred_at: z.string().min(1, 'Date and time is required'),
  lead: z.string(),
  company: z.string(),
  contact: z.string(),
  deal: z.string(),
  owner: z.string(),
});

type FormValues = z.infer<typeof schema>;
const FIELD_KEYS = [
  'type', 'subject', 'notes', 'occurred_at', 'lead', 'company', 'contact', 'deal', 'owner',
] as const;

interface ActivityFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activity?: Activity;
  /** Pre-fill values when opening in create mode. Ignored if `activity` is set
   *  (the activity's values take precedence). Used by ActivitiesPanel to scope
   *  the new activity to the current detail-page entity, and by the "Send email"
   *  CTA to pre-seed an email activity. */
  defaults?: Partial<
    Pick<ActivityInput, 'type' | 'subject' | 'lead' | 'company' | 'contact' | 'deal' | 'owner'>
  >;
  onSubmit: (input: ActivityInput) => Promise<unknown>;
}

function toValues(activity?: Activity, defaults?: ActivityFormDialogProps['defaults']): FormValues {
  if (activity) {
    return {
      type: activity.type,
      subject: activity.subject,
      notes: activity.notes,
      occurred_at: toLocalInput(activity.occurred_at ?? null),
      lead: activity.lead ? String(activity.lead) : '',
      company: activity.company ? String(activity.company) : '',
      contact: activity.contact ? String(activity.contact) : '',
      deal: activity.deal ? String(activity.deal) : '',
      owner: activity.owner ? String(activity.owner) : '',
    };
  }
  return {
    type: defaults?.type ?? 'call',
    subject: defaults?.subject ?? '',
    notes: '',
    occurred_at: toLocalInput(null),
    lead: defaults?.lead != null ? String(defaults.lead) : '',
    company: defaults?.company != null ? String(defaults.company) : '',
    contact: defaults?.contact != null ? String(defaults.contact) : '',
    deal: defaults?.deal != null ? String(defaults.deal) : '',
    owner: defaults?.owner != null ? String(defaults.owner) : '',
  };
}

export function ActivityFormDialog({ open, onOpenChange, activity, defaults, onSubmit }: ActivityFormDialogProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: toValues(activity, defaults),
  });

  const { reset } = form;
  useEffect(() => {
    if (open) reset(toValues(activity, defaults));
  }, [open, activity, defaults, reset]);

  async function handleSubmit(values: FormValues) {
    const occurredIso = fromLocalInput(values.occurred_at);
    if (!occurredIso) {
      form.setError('occurred_at', { message: 'Enter a valid date and time.' });
      return;
    }
    const input: ActivityInput = {
      type: values.type,
      subject: values.subject,
      notes: values.notes,
      occurred_at: occurredIso,
      lead: values.lead ? Number(values.lead) : null,
      company: values.company ? Number(values.company) : null,
      contact: values.contact ? Number(values.contact) : null,
      deal: values.deal ? Number(values.deal) : null,
      owner: values.owner ? Number(values.owner) : null,
    };
    try {
      await onSubmit(input);
      onOpenChange(false);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{activity ? 'Edit activity' : 'Log activity'}</DialogTitle>
          <DialogDescription>Record an interaction and link it to CRM records.</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <FormSection title="Details" description="What happened?">
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Type</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {ACTIVITY_TYPES.map((t) => (
                            <SelectItem key={t.value} value={t.value}>
                              {t.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="occurred_at"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>When</FormLabel>
                      <FormControl>
                        <Input type="datetime-local" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="subject"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Subject</FormLabel>
                    <FormControl>
                      <Input placeholder="Discovery call with Acme" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes</FormLabel>
                    <FormControl>
                      <Textarea rows={3} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormSection>

            <FormSection title="Context" description="Link to CRM records.">
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="lead"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Lead</FormLabel>
                      <FormControl>
                        <EntitySelect
                          queryKey="activity-lead-picker"
                          value={field.value}
                          onChange={field.onChange}
                          search={searchLeads}
                          resolveLabel={resolveLeadLabel}
                          placeholder="No lead"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="company"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company</FormLabel>
                      <FormControl>
                        <EntitySelect
                          queryKey="activity-company-picker"
                          value={field.value}
                          onChange={field.onChange}
                          search={searchCompanies}
                          resolveLabel={resolveCompanyLabel}
                          placeholder="No company"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="contact"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact</FormLabel>
                      <FormControl>
                        <EntitySelect
                          queryKey="activity-contact-picker"
                          value={field.value}
                          onChange={field.onChange}
                          search={searchContacts}
                          resolveLabel={resolveContactLabel}
                          placeholder="No contact"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="deal"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Deal</FormLabel>
                      <FormControl>
                        <EntitySelect
                          queryKey="activity-deal-picker"
                          value={field.value}
                          onChange={field.onChange}
                          search={searchDeals}
                          resolveLabel={resolveDealLabel}
                          placeholder="No deal"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="owner"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-1.5">
                        Owner
                        <InfoHint text="The sales person responsible." />
                      </FormLabel>
                      <FormControl>
                        <MemberSelect
                          queryKey="activity-owner-picker"
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Me"
                          roles={CRM_OWNER_ROLES}
                          clearable
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </FormSection>

            {form.formState.errors.root ? (
              <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
            ) : null}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? 'Saving…' : activity ? 'Save changes' : 'Log activity'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
